import {useState, useDebugValue, useLayoutEffect} from 'react';
import {useRefWithListeners} from './dom';

/*
    useDropTarget
*/

export const useDropTarget = ({type = 'default', onDrop, canDrop: canDropPredicate}) => {
    const [isOver, setOver] = useState(false);
    const [canDrop, setCanDrop] = useState(true);
    const [dragCoords, setDragCoords] = useState(null);

    const ref = useRefWithListeners({
        dragover: e => {
            // called every ~100ms when a DragSource is over a DropTarget
            if (e.dataTransfer.types.includes(type)) {
                e.preventDefault();
                if (canDropPredicate) {
                    if (canDropPredicate()) {
                        setCanDrop(true);
                    } else {
                        setCanDrop(false);
                    }
                } else {
                    setCanDrop(true);
                }
                setDragCoords({x: e.x, y: e.y, e});
                return false;
            }
            return true;
        },
        dragenter: e => { // eslint-disable-line no-unused-vars
            setOver(true);
        },
        dragleave: e => { // eslint-disable-line no-unused-vars
            setOver(false);
        },
        drop: e => {
            if (e.stopPropagation) {
                e.stopPropagation();
            }

            if (onDrop) {
                let json = e.dataTransfer.getData(type);
                let obj = json ? JSON.parse(json) : undefined;
                onDrop(obj, e.x, e.y);
            }
            return false;
        }
    });

    useDebugValue(isOver, () => isOver ? 'over' : 'not over');

    return [{isOver, canDrop, ...dragCoords}, ref];
};

/*
    useDragSource
*/

export const useDragSource = ({type = 'default',
                               data,
                               hoverImage // a ref to an <img /> tag
                              }) => {
    const [dragCoords, setDragCoords] = useState({});
    const [isDragging, setDragging] = useState(false);

    const ref = useRefWithListeners({
        dragstart: e => {
            // Called when the drag starts
            setDragging(true);
            if (data) {
                e.dataTransfer.setData(type, JSON.stringify(data));
            }
            if (hoverImage) {
                let img = hoverImage.current;
                if (img) {
                    let r = img.getBoundingClientRect();
                    e.dataTransfer.setDragImage(img, r.width / 2, r.height / 2);
                } else {
                    // TODO: fallback
                }
            }
        },
        drag: e => {
            // Called on every mouse motion when dragging
            setDragCoords({x: e.x, y: e.y});
        },
        dragend: e => {
            // Called when the drag ends
            e.dataTransfer.clearData();
            setDragging(false);
            setDragCoords({});
        }
    });

    useLayoutEffect(() => {
        if (ref.current) {
            let old = ref.current.getAttribute('draggable');
            if (old !== 'true') {
                ref.current.setAttribute('draggable', 'true');
                return () => ref.current.setAttribute('draggable', old);
            }
        }
        return () => {};
    }, [ref.current]);

    useDebugValue(data, data => JSON.stringify(data, null, 2));

    return [{isDragging, ...dragCoords}, ref];
};

